import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Plus,
  Users,
  FolderOpen,
  Trash2,
  ChevronRight,
  Crown,
  Edit3,
  Eye,
  UserPlus,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { useGroups, useCreateGroup, useDeleteGroup } from "../../application/hooks/useGroups";
import { useTotalBalance } from "../../application/hooks/useTotalBalance";
import { QuickExpenseDialog } from "../components/QuickExpenseDialog";
import { useConfirm } from "@tabipla/components";
import { useOnboarding } from "../../application/hooks/useOnboarding";
import { OnboardingGuide } from "../components/OnboardingGuide";
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  EmptyState,
} from "@tabipla/ui";
import { WarittoLayout } from "../layouts/WarittoLayout";
import { ErrorState } from "../components/ErrorState";
import { GroupsListSkeleton } from "../components/Skeleton";

export function GroupsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: groups, isLoading, error, refetch } = useGroups();
  const { data: balanceSummary } = useTotalBalance();
  const createMutation = useCreateGroup();
  const deleteMutation = useDeleteGroup();
  const { confirm, ConfirmDialog } = useConfirm();
  const { hasCreatedFirstGroup, markFirstGroupCreated, markMemberInvited } = useOnboarding();

  // Create a map of groupId -> balance for quick lookup
  const balanceByGroupId = useMemo(() => {
    if (!balanceSummary?.groups) return new Map<string, number>();
    return new Map(balanceSummary.groups.map((g) => [g.groupId, g.balance]));
  }, [balanceSummary?.groups]);

  const formatBalance = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(Math.abs(amount));
  };

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQuickExpense, setShowQuickExpense] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showInvitePrompt, setShowInvitePrompt] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newlyCreatedGroupId, setNewlyCreatedGroupId] = useState<string | null>(null);
  const [newlyCreatedGroupName, setNewlyCreatedGroupName] = useState("");

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    try {
      const group = await createMutation.mutateAsync({
        name: newGroupName,
        description: newGroupDescription || null,
      });
      toast.success(t("groups.createSuccess", "わりかんを作成しました"));
      setShowCreateDialog(false);

      // Show onboarding guide for first-time users, or invite prompt for returning users
      if (group) {
        setNewlyCreatedGroupId(group.id);
        setNewlyCreatedGroupName(newGroupName);
        if (!hasCreatedFirstGroup) {
          markFirstGroupCreated();
          setShowOnboarding(true);
        } else {
          // For returning users, show a simpler invite prompt
          setShowInvitePrompt(true);
        }
      }

      setNewGroupName("");
      setNewGroupDescription("");
    } catch {
      toast.error(t("groups.createError", "作成に失敗しました"));
    }
  };

  const handleDeleteGroup = async (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await confirm({
      title: t("groups.deleteTitle", "わりかんを削除"),
      description: t("groups.deleteConfirm"),
      confirmText: t("common.delete", "削除"),
      variant: "destructive",
    });

    if (confirmed) {
      try {
        await deleteMutation.mutateAsync(groupId);
        toast.success(t("groups.deleteSuccess", "わりかんを削除しました"));
      } catch {
        toast.error(t("groups.deleteError", "削除に失敗しました"));
      }
    }
  };

  const getRoleStyle = (role: string) => {
    switch (role) {
      case "owner":
        return {
          cardBg:
            "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 dark:border-emerald-800 dark:from-emerald-950/40 dark:to-teal-950/40",
          iconBg: "bg-emerald-500 shadow-lg shadow-emerald-500/30",
          badgeBg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
          Icon: Crown,
        };
      case "editor":
        return {
          cardBg:
            "border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 dark:border-blue-800 dark:from-blue-950/40 dark:to-indigo-950/40",
          iconBg: "bg-blue-500 shadow-lg shadow-blue-500/30",
          badgeBg: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
          Icon: Edit3,
        };
      default:
        return {
          cardBg: "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50",
          iconBg: "bg-gray-400 dark:bg-gray-600",
          badgeBg: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
          Icon: Eye,
        };
    }
  };

  if (isLoading) {
    return (
      <WarittoLayout title={t("groups.title")}>
        <div className="space-y-6">
          <div className="hidden lg:block">
            <h1 className="text-2xl font-bold tracking-tight">{t("groups.title")}</h1>
            <p className="text-muted-foreground">{t("groups.subtitle")}</p>
          </div>
          <GroupsListSkeleton count={3} />
        </div>
      </WarittoLayout>
    );
  }

  if (error) {
    return (
      <WarittoLayout title={t("groups.title")}>
        <ErrorState error={error} onRetry={refetch} />
      </WarittoLayout>
    );
  }

  const hasGroups = groups && groups.length > 0;

  // FAB actions
  const fabActions = hasGroups
    ? [
        {
          icon: Plus,
          label: t("expenses.addExpense", "出費を追加"),
          onClick: () => setShowQuickExpense(true),
        },
        {
          icon: UserPlus,
          label: t("groups.createNew", "新しいわりかん"),
          onClick: () => setShowCreateDialog(true),
        },
      ]
    : [
        {
          icon: UserPlus,
          label: t("groups.createNew", "新しいわりかん"),
          onClick: () => setShowCreateDialog(true),
        },
      ];

  return (
    <WarittoLayout title={t("groups.title")} fabActions={fabActions}>
      <div className="animate-fade-in space-y-6">
        {/* Header with Create Button */}
        <div className="flex items-center justify-between">
          <div className="hidden lg:block">
            <h1 className="text-2xl font-bold tracking-tight">{t("groups.title")}</h1>
            <p className="text-muted-foreground">{t("groups.subtitle")}</p>
          </div>
          {hasGroups && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCreateDialog(true)}
              className="ml-auto"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              {t("groups.createNew")}
            </Button>
          )}
        </div>

        {/* Groups List */}
        {!groups || groups.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={t("groups.noGroups")}
            description={t("groups.noGroupsDescription", "わりかんを作成して始めましょう")}
            action={{
              label: t("groups.createNew"),
              onClick: () => setShowCreateDialog(true),
              icon: Plus,
            }}
          />
        ) : (
          <div className="space-y-3">
            {groups.map((group) => {
              const roleStyle = getRoleStyle(group.myRole);
              const RoleIcon = roleStyle.Icon;
              const balance = balanceByGroupId.get(group.id) ?? 0;
              const hasBalance = balance !== 0;

              return (
                <div
                  key={group.id}
                  className={`cursor-pointer overflow-hidden rounded-xl border transition-all hover:shadow-lg active:scale-[0.98] ${roleStyle.cardBg}`}
                  onClick={() => navigate(`/groups/${group.id}`)}
                >
                  <div className="flex items-center gap-3 p-4">
                    {/* Group Icon */}
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${roleStyle.iconBg}`}
                    >
                      <Users className="h-6 w-6 text-white" />
                    </div>

                    {/* Group Info */}
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-gray-900 dark:text-white">
                        {group.name}
                      </h3>
                      {group.description && (
                        <p className="mt-0.5 truncate text-sm text-muted-foreground">
                          {group.description}
                        </p>
                      )}
                      <div className="mt-1.5 flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {group.memberCount}
                          {t("groups.members", "人")}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${roleStyle.badgeBg}`}
                        >
                          <RoleIcon className="h-3 w-3" />
                          {t(`groups.roles.${group.myRole}`)}
                        </span>
                      </div>
                    </div>

                    {/* Balance Display */}
                    {hasBalance && (
                      <div className="shrink-0 text-right">
                        <div
                          className={`flex items-center gap-1 text-lg font-bold tabular-nums ${
                            balance > 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {balance > 0 ? (
                            <ArrowDownRight className="h-4 w-4" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4" />
                          )}
                          {formatBalance(balance)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {balance > 0
                            ? t("balance.toReceive", "受け取り")
                            : t("balance.toPay", "支払い")}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex shrink-0 items-center gap-1">
                      {group.myRole === "owner" && (
                        <button
                          onClick={(e) => handleDeleteGroup(group.id, e)}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-600" />
              {t("groups.createTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("groups.createDescription", "新しいわりかんを作成しましょう")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateGroup}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("groups.name")}</label>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t("groups.namePlaceholder")}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("groups.description")}
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({t("common.optional")})
                  </span>
                </label>
                <Input
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder={t("groups.descriptionPlaceholder")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1 sm:flex-none"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || !newGroupName.trim()}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 sm:flex-none"
              >
                {createMutation.isPending ? t("groups.creating") : t("groups.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />

      {/* Quick Expense Dialog */}
      {hasGroups && (
        <QuickExpenseDialog
          open={showQuickExpense}
          onOpenChange={setShowQuickExpense}
          groups={groups}
        />
      )}

      {/* Onboarding Guide - shown after first group creation */}
      <OnboardingGuide
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
        groupName={newlyCreatedGroupName}
        onInviteMembers={() => {
          setShowOnboarding(false);
          markMemberInvited();
          if (newlyCreatedGroupId) {
            navigate(`/groups/${newlyCreatedGroupId}?invite=true`);
          }
        }}
        onAddExpense={() => {
          setShowOnboarding(false);
          if (newlyCreatedGroupId) {
            navigate(`/groups/${newlyCreatedGroupId}?addExpense=true`);
          }
        }}
        onSkip={() => {
          setShowOnboarding(false);
          if (newlyCreatedGroupId) {
            navigate(`/groups/${newlyCreatedGroupId}`);
          }
        }}
      />

      {/* Invite Prompt - shown after group creation for returning users */}
      <Dialog open={showInvitePrompt} onOpenChange={setShowInvitePrompt}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <UserPlus className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <DialogTitle>{t("groups.invitePromptTitle", "メンバーを招待しますか？")}</DialogTitle>
            <DialogDescription>
              {t(
                "groups.invitePromptDescription",
                "「{{groupName}}」に友達を招待して、一緒にわりかんを始めましょう",
                { groupName: newlyCreatedGroupName }
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              onClick={() => {
                setShowInvitePrompt(false);
                if (newlyCreatedGroupId) {
                  navigate(`/groups/${newlyCreatedGroupId}?invite=true`);
                }
              }}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              {t("groups.inviteNow", "今すぐ招待")}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => {
                setShowInvitePrompt(false);
                if (newlyCreatedGroupId) {
                  navigate(`/groups/${newlyCreatedGroupId}`);
                }
              }}
            >
              {t("groups.skipInvite", "あとで招待")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WarittoLayout>
  );
}
